// Direct browser -> Kite websocket client for live option/spot LTP ticks,
// replacing REST polling in OptionChainPicker.js. Deliberate tradeoff: the
// api_key/access_token handed back by /portfolio/kite_ws_session is a live
// trading credential exposed to the browser so this connection can auth
// straight against wss://ws.kite.trade, instead of proxying every tick
// through the backend.
//
// Module-level singleton: every caller across the app shares one Kite
// connection. Tokens are reference-counted so the same instrument being
// watched by more than one component only gets subscribed once.
// Duplicated from OptionsBasketContext.js's OPTIONS_API_BASE rather than
// imported — that module imports subscribeTick from here, so importing back
// would be a circular dependency. Same value, kept in sync manually.
const OPTIONS_API_BASE = 'https://live1.finstrats.com';
const WS_URL = 'wss://ws.kite.trade';

let ws = null;
let connectPromise = null;
let manuallyClosed = false;
let reconnectAttempts = 0;
let reconnectTimer = null;
let authFailed = false;

// Ticks only matter while the market's actually moving — connecting before
// pre-open, leaving the socket open long after close, or holding it open all
// weekend just burns a live trading credential's session for no reason.
// Mon-Fri, 9:07-15:40 local time (this is an India-only tool, same
// unqualified-local-time convention isMarketOpen() elsewhere already uses).
const TRADING_START_MIN = 9 * 60 + 7;
const TRADING_END_MIN = 15 * 60 + 40;
const isWithinTradingWindow = () => {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    if (day === 0 || day === 6) return false;
    const mins = now.getHours() * 60 + now.getMinutes();
    return mins >= TRADING_START_MIN && mins < TRADING_END_MIN;
};

const tokenRefCounts = new Map(); // instrument_token -> listener count
const tickListeners = new Map();  // instrument_token -> Set<(price) => void>

const fetchSession = async () => {
    const res = await fetch(`${OPTIONS_API_BASE}/portfolio/kite_ws_session`);
    const data = await res.json();
    if (!res.ok || !data.access_token) throw new Error(data.error || 'Failed to fetch Kite ws session');
    return data;
};

// Every packet's first 8 bytes are instrument_token(int32) + last_price(int32,
// paise) regardless of mode (ltp/quote/full share that prefix) — reading just
// those 8 bytes means we don't need to parse the rest of quote/full packets.
const parseBinaryTicks = (buffer) => {
    const dv = new DataView(buffer);
    if (dv.byteLength < 2) return [];
    const numPackets = dv.getInt16(0, false);
    let offset = 2;
    const ticks = [];
    for (let i = 0; i < numPackets; i += 1) {
        if (offset + 2 > dv.byteLength) break;
        const packetLength = dv.getInt16(offset, false);
        offset += 2;
        if (offset + packetLength > dv.byteLength) break;
        if (packetLength >= 8) {
            const instrument_token = dv.getInt32(offset, false);
            const last_price = dv.getInt32(offset + 4, false) / 100;
            ticks.push({ instrument_token, last_price });
        }
        offset += packetLength;
    }
    return ticks;
};

const dispatchTick = ({ instrument_token, last_price }) => {
    const listeners = tickListeners.get(instrument_token);
    if (!listeners) return;
    listeners.forEach((cb) => cb(last_price));
};

const sendSubscribe = (tokens) => {
    if (ws && ws.readyState === WebSocket.OPEN && tokens.length) {
        ws.send(JSON.stringify({ a: 'subscribe', v: tokens }));
        ws.send(JSON.stringify({ a: 'mode', v: ['ltp', tokens] }));
    }
};

const sendUnsubscribe = (tokens) => {
    if (ws && ws.readyState === WebSocket.OPEN && tokens.length) {
        ws.send(JSON.stringify({ a: 'unsubscribe', v: tokens }));
    }
};

const scheduleReconnect = () => {
    if (reconnectTimer || manuallyClosed || authFailed) return;
    const delay = Math.min(1000 * 2 ** reconnectAttempts, 15000);
    reconnectAttempts += 1;
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        ensureConnection();
    }, delay);
};

function ensureConnection() {
    if (authFailed) return null;
    if (!isWithinTradingWindow()) return null;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return connectPromise;
    // A connection attempt is already in flight (fetchSession() pending, `ws`
    // not assigned yet since that only happens once the fetch resolves).
    // Without this, N synchronous subscribeTick() calls in the same tick —
    // e.g. mapping over 40 strike contracts on first load — would each see
    // `ws` still null and independently kick off their own fetchSession() +
    // new WebSocket(), spawning dozens of parallel connection attempts
    // instead of sharing the one in flight.
    if (connectPromise) return connectPromise;

    manuallyClosed = false;
    connectPromise = fetchSession().then(({ api_key, access_token }) => {
        const socket = new WebSocket(`${WS_URL}?api_key=${api_key}&access_token=${access_token}`);
        socket.binaryType = 'arraybuffer';

        socket.onopen = () => {
            reconnectAttempts = 0;
            const tokens = Array.from(tokenRefCounts.keys());
            sendSubscribe(tokens);
        };

        socket.onmessage = (event) => {
            if (!(event.data instanceof ArrayBuffer)) return;
            parseBinaryTicks(event.data).forEach(dispatchTick);
        };

        socket.onclose = (event) => {
            ws = null;
            connectPromise = null; // clear so a later ensureConnection() call can retry
            if (manuallyClosed) return;
            // Kite closes with 4xxx codes for auth/session problems — retrying
            // with the same (now-known-bad) token would just loop forever.
            if (event.code >= 4000 && event.code < 4100) {
                authFailed = true;
                console.error('Kite ticker auth failed, not retrying:', event.code, event.reason);
                return;
            }
            scheduleReconnect();
        };

        socket.onerror = () => { /* onclose fires right after with the real reason */ };

        ws = socket;
    }).catch((err) => {
        console.error('Kite ticker connection failed:', err);
        ws = null;
        connectPromise = null; // clear so scheduleReconnect's retry can actually reconnect
        scheduleReconnect();
    });

    return connectPromise;
}

// Forces the socket shut the moment the trading window ends, rather than
// waiting for it to happen to close/error on its own — subscriptions
// (tokenRefCounts/tickListeners) are left untouched so the same set just
// gets resubscribed automatically (via onopen) whenever the window next
// opens, no page reload needed.
function closeForOutsideWindow() {
    if (ws) {
        manuallyClosed = true; // stops onclose from scheduling a reconnect
        ws.close();
        ws = null;
    }
    connectPromise = null;
    if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    reconnectAttempts = 0;
}

// Periodic check so both edges of the window are self-serve: a component
// that subscribed before 9:07 gets connected the moment the window opens
// (ensureConnection() resets manuallyClosed=false itself), and a connection
// still open at 15:40 gets torn down without needing anything to unmount.
setInterval(() => {
    if (authFailed) return;
    if (isWithinTradingWindow()) {
        if (!ws && !connectPromise && tokenRefCounts.size > 0) ensureConnection();
    } else if (ws) {
        closeForOutsideWindow();
    }
}, 30000);

// Subscribe to live LTP for one instrument_token. Returns an unsubscribe fn.
export const subscribeTick = (instrumentToken, onPrice) => {
    if (!instrumentToken) return () => {};
    ensureConnection();

    if (!tickListeners.has(instrumentToken)) tickListeners.set(instrumentToken, new Set());
    tickListeners.get(instrumentToken).add(onPrice);

    const prevCount = tokenRefCounts.get(instrumentToken) || 0;
    tokenRefCounts.set(instrumentToken, prevCount + 1);
    if (prevCount === 0) sendSubscribe([instrumentToken]);

    return () => {
        const listeners = tickListeners.get(instrumentToken);
        if (listeners) {
            listeners.delete(onPrice);
            if (listeners.size === 0) tickListeners.delete(instrumentToken);
        }
        const count = (tokenRefCounts.get(instrumentToken) || 1) - 1;
        if (count <= 0) {
            tokenRefCounts.delete(instrumentToken);
            sendUnsubscribe([instrumentToken]);
        } else {
            tokenRefCounts.set(instrumentToken, count);
        }
    };
};
